import { ChangeEvent, useEffect, useState } from "react";
import {
  CButton, CButtonGroup, CCard, CCardBody, CCardHeader, CCol, CDataTable, CFormGroup, CInput, CLabel,
  CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CPagination, CRow, CSelect, CTextarea, CTooltip,
} from "@coreui/react";
import { getItem, getList, postItem, updateItem } from "../../api/generics";
import { incidentDelivery, incidentProgress, SifenIncident } from "../../models/sifen-incident";
import { SUCCESS } from "../../utils/constants/tags";
import SelectBootstrap from "../react-select-bootstrap/SelectBootstrap";

import SifenIncidentConversationModal from "./SifenIncidentConversationModal";

const PAGE_SIZE = 10;
const fields = [
  { key: "issueLabel", _classes: ["text-center"], label: "Incidencia" },
  { key: "taxPayerName", _classes: ["text-center"], label: "Contribuyente" },
  { key: "cdc", _classes: ["text-center"], label: "CDC" },
  { key: "progressStatus", _classes: ["text-center"], label: "Seguimiento" },
  { key: "deliveryStatus", _classes: ["text-center"], label: "Correo" },
  { key: "actions", _classes: ["text-center"], label: "Acciones", filter: false },
];
const progressOptions = Object.entries(incidentProgress).map(([value, label]) => ({ value, label }));
const deliveryOptions = Object.entries(incidentDelivery).map(([value, label]) => ({ value, label }));
const date = (value?: string | null) => value ? new Date(value).toLocaleString("es-PY") : "—";

const SifenIncidentCrud = () => {
  const [items, setItems] = useState<SifenIncident[]>([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [progressFilter, setProgressFilter] = useState<string[]>([]);
  const [deliveryFilter, setDeliveryFilter] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [conversationId, setConversationId] = useState<number>();
  const [selectedId, setSelectedId] = useState<number>();
  const [detail, setDetail] = useState<SifenIncident>();
  const [detailError, setDetailError] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("open");
  const [ticket, setTicket] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const filters = new Map<string, string | string[]>([
      ["progress_status", progressFilter], ["delivery_status", deliveryFilter], ["search", search],
    ]);
    getList<SifenIncident>("/sifen_reports/admin/", PAGE_SIZE, offset, filters)
      .then((result) => {
        if (!active) return;
        if (result.status !== SUCCESS || !result.data) {
          setError(result.detail || "No se pudieron cargar las incidencias.");
          setItems([]);
          setCount(0);
          return;
        }
        setItems(result.data.items);
        setCount(result.data.count);
      })
      .catch(() => { if (active) { setItems([]); setCount(0); setError("No se pudieron cargar las incidencias."); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [offset, progressFilter, deliveryFilter, search, reload]);

  const loadDetail = async (id: number) => {
    setSelectedId(id);
    setDetailError("");
    setDetailLoading(true);
    try {
      const result = await getItem<SifenIncident>(`/sifen_reports/admin/${id}/`);
      if (result.status !== SUCCESS || !result.data) {
        setDetailError(result.detail || "No se pudo cargar la incidencia.");
        return;
      }
      setDetail(result.data);
      setProgress(result.data.progressStatus);
      setTicket(result.data.supportTicket || "");
      setNote("");
    } catch {
      setDetailError("No se pudo cargar la incidencia.");
    } finally {
      setDetailLoading(false);
    }
  };

  const save = async (retry: boolean) => {
    if (!detail || saving) return;
    setSaving(true);
    setDetailError("");
    try {
      const result = retry
        ? await postItem(`/sifen_reports/admin/${detail.id}/retry/`, {})
        : await updateItem(`/sifen_reports/admin/${detail.id}/`, { progressStatus: progress, supportTicket: ticket, note });
      if (result.status !== SUCCESS) {
        setDetailError(result.detail || "No se pudo guardar el seguimiento.");
        return;
      }
      await loadDetail(detail.id);
      setReload((value) => value + 1);
    } catch {
      setDetailError("No se pudo guardar el seguimiento.");
    } finally {
      setSaving(false);
    }
  };

  return <>
    <CCard>
      <CCardHeader><h1 className="h4 mb-0">Incidencias SIFEN</h1></CCardHeader>
      <CCardBody>
        <p>Seguimiento de incidencias reportadas por los contribuyentes y de sus correos programados.</p>
        <CRow>
          <CCol md={5}><CFormGroup>
            <CLabel htmlFor="incidence-search">Contribuyente, RUC, CDC, documento o ticket</CLabel>
            <CInput id="incidence-search" value={searchInput} maxLength={200} onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchInput(event.target.value)} />
          </CFormGroup></CCol>
          <CCol md={3}><CFormGroup>
            <CLabel htmlFor="incidence-progress-filter">Seguimiento</CLabel>
            <SelectBootstrap
              inputId="incidence-progress-filter"
              isMulti
              options={progressOptions}
              value={progressOptions.filter(({ value }) => progressFilter.includes(value))}
              placeholder="Todos los estados"
              onChange={(options) => { setProgressFilter(options.map(({ value }) => value)); setOffset(0); }}
            />
          </CFormGroup></CCol>
          <CCol md={3}><CFormGroup>
            <CLabel htmlFor="incidence-delivery-filter">Envío de correo</CLabel>
            <SelectBootstrap
              inputId="incidence-delivery-filter"
              isMulti
              options={deliveryOptions}
              value={deliveryOptions.filter(({ value }) => deliveryFilter.includes(value))}
              placeholder="Todos los envíos"
              onChange={(options) => { setDeliveryFilter(options.map(({ value }) => value)); setOffset(0); }}
            />
          </CFormGroup></CCol>
        </CRow>
        <div className="mb-3 d-flex" style={{ gap: "8px" }}>
          <CButton color="primary" onClick={() => { setSearch(searchInput.trim()); setOffset(0); }}>Buscar</CButton>
          <CButton color="secondary" disabled={loading} onClick={() => setReload((value) => value + 1)}>Actualizar</CButton>
        </div>
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        {loading && <p role="status">Cargando incidencias...</p>}
        <CDataTable
          noItemsView={{ noItems: "No hay incidencias para estos filtros.", noResults: "No hay incidencias para estos filtros." }}
          addTableClasses="table-fixed"
          fields={fields}
          items={items}
          striped
          border
          loading={loading}
          responsive
          scopedSlots={{
            issueLabel: (item: SifenIncident) => (
              <td className="text-center"><strong>#{item.id}</strong><div>{item.issueLabel}</div><small>{date(item.createdAt)}</small></td>
            ),
            taxPayerName: (item: SifenIncident) => (
              <td className="text-center"><strong>{item.taxPayerName}</strong><div>RUC: {item.taxPayerRuc}</div><small>{item.documentNumber}</small></td>
            ),
            cdc: (item: SifenIncident) => (
              <td className="text-center" style={{ minWidth: "210px", overflowWrap: "anywhere" }}>{item.cdc}</td>
            ),
            progressStatus: (item: SifenIncident) => (
              <td className="text-center">{incidentProgress[item.progressStatus]}{item.supportTicket && <div><small>Ticket: {item.supportTicket}</small></div>}</td>
            ),
            deliveryStatus: (item: SifenIncident) => (
              <td className="text-center">{incidentDelivery[item.deliveryStatus]}<div><small>Intentos: {item.attemptCount}</small></div>{item.lastError && <small className="text-danger">{item.lastError}</small>}</td>
            ),
            actions: (item: SifenIncident) => (
              <td className="text-right">
                <CButtonGroup>
                  <CTooltip content="Seguimiento">
                    <CButton
                      className="text-white"
                      color="info"
                      aria-label="Seguimiento"
                      onClick={() => { setConversationId(undefined); setDetail(undefined); void loadDetail(item.id); }}
                    >
                      <i className="fa fa-eye" aria-hidden="true" />
                    </CButton>
                  </CTooltip>
                  <CTooltip content="Conversación por correo">
                    <CButton color="primary" aria-label="Conversación por correo" onClick={() => { setSelectedId(undefined); setConversationId(item.id); }}>
                      <i className="fa fa-envelope" aria-hidden="true" />
                    </CButton>
                  </CTooltip>
                </CButtonGroup>
              </td>
            ),
          }}
        />
        <div className="d-flex justify-content-center">
          <CPagination
            pages={Math.max(1, Math.ceil(count / PAGE_SIZE))}
            activePage={offset / PAGE_SIZE + 1}
            onActivePageChange={(page: number) => setOffset((page - 1) * PAGE_SIZE)}
            className={count <= PAGE_SIZE ? "d-none" : ""}
          />
        </div>
      </CCardBody>
    </CCard>
    {conversationId !== undefined && <SifenIncidentConversationModal key={conversationId} incidentId={conversationId}
      onClose={() => setConversationId(undefined)} onSent={() => setReload(value => value + 1)} />}
    <CModal show={selectedId !== undefined} size="xl" onClose={() => { if (!saving) setSelectedId(undefined); }} closeOnBackdrop={!saving}>
      <CModalHeader closeButton={!saving}><CModalTitle>Seguimiento de incidencia #{selectedId}</CModalTitle></CModalHeader>
      <CModalBody>
        {detailError && <div className="alert alert-danger" role="alert">{detailError}</div>}
        {detailLoading && <p role="status">Cargando seguimiento...</p>}
        {detail && !detailLoading && <>
          <h2 className="h5">{detail.taxPayerName} · RUC {detail.taxPayerRuc}</h2>
          <p style={{ overflowWrap: "anywhere" }}><strong>CDC:</strong> {detail.cdc}<br /><strong>Documento:</strong> {detail.documentNumber}<br /><strong>Incidencia:</strong> {detail.issueLabel}<br /><strong>Reportada por:</strong> {detail.requestedByName}</p>
          <p><strong>Correo:</strong> {incidentDelivery[detail.deliveryStatus]} · Intentos: {detail.attemptCount}<br />Próximo intento: {date(detail.nextAttemptAt)}<br />Enviado: {date(detail.sentAt)}</p>
          {detail.lastError && <p className="alert alert-warning">{detail.lastError}</p>}
          <fieldset disabled={saving}>
            <CFormGroup><CLabel htmlFor="incidence-progress">Estado de seguimiento</CLabel>
              <CSelect id="incidence-progress" value={progress} onChange={(event: ChangeEvent<HTMLSelectElement>) => setProgress(event.target.value)}>
                {Object.entries(incidentProgress).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </CSelect>
            </CFormGroup>
            <CFormGroup><CLabel htmlFor="incidence-ticket">Número de ticket SIFEN</CLabel>
              <CInput id="incidence-ticket" maxLength={255} value={ticket} onChange={(event: ChangeEvent<HTMLInputElement>) => setTicket(event.target.value)} />
            </CFormGroup>
            <CFormGroup><CLabel htmlFor="incidence-note">Nota de seguimiento o respuesta de SIFEN</CLabel>
              <CTextarea id="incidence-note" rows={3} maxLength={4000} value={note} onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setNote(event.target.value)} />
              <small className="text-muted">Esta nota y los cambios de estado o ticket son visibles para el contribuyente.</small>
            </CFormGroup>
          </fieldset>
          <h3 className="h5 mt-4">Historial</h3>
          <ul className="list-unstyled">{detail.events?.filter(event => event.kind !== "email_received" && !event.kind?.startsWith("email_reply_")).map((event) => <li key={event.id} className="border-bottom py-2">
            <small>{date(event.createdAt)} · {event.authorName}</small><div style={{ whiteSpace: "pre-wrap" }}>{event.note}</div>
          </li>)}</ul>
          {!!detail.snapshot?.attachments.length && <details className="mt-3"><summary>Ver archivos XML</summary>
            <ul>{detail.snapshot.attachments.map(({ filename }) => <li key={filename}>{filename}</li>)}</ul>
          </details>}
        </>}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" disabled={saving} onClick={() => setSelectedId(undefined)}>Cerrar</CButton>
        {detail && !detailLoading && <>
          {detail.deliveryStatus === "failed" && !["resolved", "closed"].includes(detail.progressStatus) && <CButton color="warning" disabled={saving} onClick={() => save(true)}>Reprogramar envío</CButton>}
          <CButton color="primary" disabled={saving} onClick={() => save(false)}>Guardar seguimiento</CButton>
        </>}
      </CModalFooter>
    </CModal>
  </>;
};

export default SifenIncidentCrud;
